import { Node } from "api";
import { WithContext } from "state/action-types";
import { dispatch, select } from "state/store-sagas";
import { saga } from "state/saga";
import { nodeCardsPreload } from "state/nodecards/actions";
import {
    ReactionsDialogPastReactionsLoadAction,
    reactionsDialogPastReactionsLoaded,
    reactionsDialogPastReactionsLoadFailed,
    ReactionsDialogTotalsLoadAction,
    reactionsDialogTotalsLoaded,
    reactionsDialogTotalsLoadFailed,
    ReactionVerifyAction,
    reactionVerifyFailed
} from "state/reactionsdialog/actions";
import { errorThrown } from "state/error/actions";
import { getReactionsDialogPosting } from "state/reactionsdialog/selectors";
import { REL_HOME } from "util/rel-node-name";
import { notNull } from "util/misc";

export default [
    saga("REACTIONS_DIALOG_PAST_REACTIONS_LOAD", null, reactionsDialogPastReactionsLoadSaga),
    saga("REACTIONS_DIALOG_TOTALS_LOAD", null, reactionsDialogTotalsLoadSaga),
    saga(
        "REACTION_VERIFY",
        payload => `${payload.postingId}:${payload.commentId}:${payload.ownerName}`,
        reactionVerifySaga
    )
];

async function reactionsDialogPastReactionsLoadSaga(
    action: WithContext<ReactionsDialogPastReactionsLoadAction>
): Promise<void> {
    const {generation, nodeName, posting, commentId, negative, before, emoji} = select(state => ({
        generation: state.reactionsDialog.generation,
        nodeName: state.reactionsDialog.nodeName,
        posting: getReactionsDialogPosting(state),
        commentId: state.reactionsDialog.commentId,
        negative: state.reactionsDialog.negative,
        before: state.reactionsDialog.reactions[state.reactionsDialog.activeTab ?? 0]?.after,
        emoji: state.reactionsDialog.activeTab
    }));
    if (nodeName == null || posting == null) {
        return;
    }
    const rNodeName = posting.receiverName ?? nodeName;
    const rPostingId = posting.receiverPostingId ?? posting.id;
    try {
        const slice = commentId == null
            ? await Node.getPostingReactionsSlice(action, rNodeName, rPostingId, negative, emoji, before, 40)
            : await Node.getCommentReactionsSlice(action, rNodeName, rPostingId, commentId, negative, emoji, before, 40);
        if (select(state => state.reactionsDialog.generation) !== generation) {
            return;
        }
        dispatch(reactionsDialogPastReactionsLoaded(
            slice.reactions, posting.id, commentId, negative, emoji, slice.before, slice.after, slice.total
        ).causedBy(action));
        dispatch(nodeCardsPreload(slice.reactions.map(sr => sr.ownerName).filter(notNull)).causedBy(action));
    } catch (e) {
        if (select(state => state.reactionsDialog.generation) !== generation) {
            return;
        }
        dispatch(reactionsDialogPastReactionsLoadFailed(posting.id, commentId, negative, emoji).causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function reactionsDialogTotalsLoadSaga(action: WithContext<ReactionsDialogTotalsLoadAction>): Promise<void> {
    const {generation, totalsGeneration, nodeName, posting, commentId} = select(state => ({
        generation: state.reactionsDialog.generation,
        totalsGeneration: state.reactionsDialog.totals.generation,
        nodeName: state.reactionsDialog.nodeName,
        posting: getReactionsDialogPosting(state),
        commentId: state.reactionsDialog.commentId
    }));
    if (nodeName == null || posting == null) {
        return;
    }
    const rNodeName = posting.receiverName ?? nodeName;
    const rPostingId = posting.receiverPostingId ?? posting.id;
    try {
        const totals = commentId == null
            ? await Node.getPostingReactionTotals(action, rNodeName, rPostingId)
            : await Node.getCommentReactionTotals(action, rNodeName, rPostingId, commentId)
        if (select(state => state.reactionsDialog.generation) !== generation) {
            return;
        }
        if (select(state => state.reactionsDialog.totals.generation) !== totalsGeneration) {
            return;
        }
        dispatch(reactionsDialogTotalsLoaded(totals.positive, totals.negative).causedBy(action));
    } catch (e) {
        if (select(state => state.reactionsDialog.generation) !== generation) {
            return;
        }
        if (select(state => state.reactionsDialog.totals.generation) !== totalsGeneration) {
            return;
        }
        dispatch(reactionsDialogTotalsLoadFailed().causedBy(action));
        dispatch(errorThrown(e));
    }
}

async function reactionVerifySaga(action: WithContext<ReactionVerifyAction>): Promise<void> {
    const {postingId, commentId, ownerName} = action.payload;
    const nodeName = action.context.ownerName;
    if (nodeName == null) {
        dispatch(reactionVerifyFailed(postingId, commentId, ownerName).causedBy(action));
        return;
    }
    try {
        if (commentId == null) {
            await Node.verifyRemotePostingReaction(action, REL_HOME, nodeName, postingId, ownerName);
        } else {
            await Node.verifyRemoteCommentReaction(action, REL_HOME, nodeName, postingId, commentId, ownerName);
        }
    } catch (e) {
        dispatch(reactionVerifyFailed(postingId, commentId, ownerName).causedBy(action));
        dispatch(errorThrown(e));
    }
}
